<template>
    <!--
        THE ADMIN SHELL'S LEFT COLUMN: a brand row, the grouped menu, and the signed-in user at
        the bottom — the three things a console's sidebar is for, in the order they are looked at.

        It paints `--ui-bg-chrome` — the project surface `theme/theme.css` declares and
        the house theme separates from the canvas. THE FRAME IS ONE SURFACE, and in
        this theme that surface is the PANEL colour in both modes: white in light, and in dark
        the same mix the cards take, both standing on a darker canvas. The hairline on the
        right edge is the seam between the frame and the page. A theme that says nothing leaves
        the variable at `--ui-bg` and gets the flat single-surface shell instead.

        Opaque, not a tint over the canvas: on mobile this column is the overlay.
    -->
    <nav class="flex flex-col w-64 shrink-0 border-r border-default bg-(--ui-bg-chrome)">
        <div class="h-(--ui-header-height) shrink-0 flex items-center gap-2.5 px-4 border-b border-default">
            <img src="/logo.svg" alt="" class="size-6" >
            <div class="leading-tight min-w-0 flex-1">
                <p class="text-sm font-semibold text-highlighted truncate">{{ $t('brand.product_name') }}</p>
                <p class="font-mono text-[10px] uppercase tracking-widest text-dimmed truncate">{{ $t('brand.tagline') }}</p>
            </div>

            <!--
                THE WAY OUT, on mobile only. Open, this column IS the overlay and it covers the
                topbar's hamburger, so the affordance that opened the menu cannot also close it.
                The X lives where the eye already is — in the brand row, opposite the mark.
            -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-x"
                :aria-label="$t('layout.close_menu')"
                class="-mr-1 shrink-0"
                @click="closeMenu"
            />
        </div>

        <div class="flex flex-col gap-5 flex-1 overflow-y-auto px-3 py-4">
            <div v-for="(group, index) in groups" :key="`group-${index}`" class="flex flex-col gap-1">
                <p
                    v-if="group.labelKey"
                    class="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-widest text-muted"
                >{{ $t(group.labelKey) }}</p>

                <UNavigationMenu
                    orientation="vertical"
                    :items="group.items"
                    :ui="{ link: 'py-2' }"
                />
            </div>
        </div>

        <!--
            The signed-in user, where an admin console puts it. It carries the sign-out action
            too — one deliberate place for it, so the topbar does not repeat the same menu.
        -->
        <div class="shrink-0 border-t border-default p-3">
            <UDropdownMenu :items="userMenu" :content="{ align: 'start', side: 'top' }" class="w-full">
                <button
                    type="button"
                    class="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-elevated transition-colors"
                >
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                        {{ initials }}
                    </span>

                    <span class="min-w-0 flex-1 leading-tight">
                        <span class="block truncate text-sm font-medium text-highlighted">{{ userName }}</span>
                        <span class="block truncate font-mono text-[10px] uppercase tracking-wider text-muted" :title="userRole">{{ userRole }}</span>
                    </span>

                    <UIcon name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
                </button>
            </UDropdownMenu>
        </div>
    </nav>
</template>

<script lang="ts" setup>
    import type { NavigationMenuItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { NAV_GROUPS, useLayout } from '@/layouts/layout'
    import { accessLine } from '@/libs/user'
    import { useAppStore } from '@/stores/app'

    /**
     * The administration menu. The MODEL lives in `layouts/layout.ts` (`NAV_GROUPS`), because
     * the topbar titles the page from the same list — one list, so a section can never be named
     * one thing in the menu and another in the title bar.
     *
     * It is a `UNavigationMenu orientation="vertical"` per group, not one menu with `type:
     * 'label'` rows: a group is a separate list with its own header, and rendering the header
     * ourselves is what lets it be the small mono uppercase signpost the house theme asks for
     * rather than the component's own bold label slot.
     *
     * THE ACTIVE ROW IS A SOFT PRIMARY PLATE. The component's `pill` variant already colours an
     * active row's label AND its leading icon `text-primary` (its `color="primary"` default);
     * what it paints behind them is `before:bg-elevated`, a neutral plate. One class per item
     * turns that into `before:bg-primary/10` — appended AFTER the variant's own class, so
     * tailwind-merge keeps the later of the two `before:bg-*` utilities — and the row becomes
     * the tinted plate the shell uses everywhere else for "this one".
     *
     * `active` is set explicitly rather than left to the router, so a DETAIL route
     * (`/users/:id`) keeps its section lit.
     */
    const { t }                   = useI18n()
    const route                   = useRoute()
    const router                  = useRouter()
    const appStore                = useAppStore()
    const { isMobile, closeMenu } = useLayout()

    const groups = computed((): { labelKey: string | null, items: NavigationMenuItem[] }[] =>
        // NALOGE: a module's section only for the people who have the module, the
        // administration only for a portal administrator.
        NAV_GROUPS.filter((group) =>
            (!group.adminOnly || appStore.isPortalAdmin()) && (!group.module || appStore.hasModule(group.module)),
        ).map((group) => ({
            labelKey: group.labelKey,
            items: group.items.map((item): NavigationMenuItem => {
                const active = item.routes.includes(String(route.name ?? ''))

                return {
                    label:    t(item.labelKey),
                    icon:     item.icon,
                    to:       { name: item.name },
                    active,
                    class:    active ? 'before:bg-primary/10' : '',
                    onSelect: () => { closeMenu() },
                }
            }),
        })))

    const userName = computed((): string => appStore.userData?.name || appStore.userData?.username || '')

    /** NALOGE: the second line of the user row — "Skrbnik portala", or the modules with their roles. */
    const userRole = computed((): string => appStore.userData
        ? accessLine(appStore.userData.portal_admin, appStore.userData.modules)
        : '')

    const initials = computed((): string => userName.value
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(''))

    // NALOGE: the profile — name, e-mail, notifications, password, passkeys — lives here, on
    // the person's own row, rather than as a menu section.
    const userMenu = computed(() => [[{
        label:    t('nav.profile'),
        icon:     'i-lucide-user-round-cog',
        onSelect: () => {
            closeMenu()
            void router.push({ name: 'profile' })
        },
    }], [{
        label:    t('general.sign_out'),
        icon:     'i-lucide-log-out',
        onSelect: () => {
            appStore.setLogout()
            void router.push({ name: 'login' })
        },
    }]])
</script>
